import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useI18n } from "@/lib/i18n";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

function upsert(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(attrs.tag || "meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => { if (k !== "tag" && v != null) el.setAttribute(k, v); });
  return el;
}

const SITE_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";
const DEFAULT_IMG = `${SITE_ORIGIN}/og-image.png`;

export default function Seo({ title, description, image, type = "website", jsonLd, noindex = false }) {
  const { lang } = useI18n();
  const { pathname } = useLocation();
  useEffect(() => {
    const fullTitle = title ? `${title} | JonuzovicDesign` : "JonuzovicDesign — Websites. Mobile Apps. Unternehmenssoftware.";
    const desc = description || (lang === "de"
      ? "JonuzovicDesign entwickelt professionelle Websites, mobile Apps und maßgeschneiderte Unternehmenssoftware — entwickelt für Ihren digitalen Erfolg."
      : "JonuzovicDesign builds professional websites, mobile apps and custom business software — designed for your success.");
    const url = `${BASE}${pathname}`;
    const img = image || DEFAULT_IMG;

    document.title = fullTitle;
    document.documentElement.lang = lang;
    upsert('meta[name="description"]', { tag: "meta", name: "description", content: desc });
    upsert('link[rel="canonical"]', { tag: "link", rel: "canonical", href: url });
    upsert('meta[name="robots"]', { tag: "meta", name: "robots", content: noindex ? "noindex,nofollow" : "index,follow" });

    upsert('meta[property="og:title"]', { tag: "meta", property: "og:title", content: fullTitle });
    upsert('meta[property="og:description"]', { tag: "meta", property: "og:description", content: desc });
    upsert('meta[property="og:type"]', { tag: "meta", property: "og:type", content: type });
    upsert('meta[property="og:url"]', { tag: "meta", property: "og:url", content: url });
    upsert('meta[property="og:image"]', { tag: "meta", property: "og:image", content: img });
    upsert('meta[property="og:image:secure_url"]', { tag: "meta", property: "og:image:secure_url", content: img });
    upsert('meta[property="og:site_name"]', { tag: "meta", property: "og:site_name", content: "JonuzovicDesign" });
    upsert('meta[property="og:locale"]', { tag: "meta", property: "og:locale", content: lang === "de" ? "de_DE" : "en_US" });
    upsert('meta[name="twitter:card"]', { tag: "meta", name: "twitter:card", content: "summary_large_image" });
    upsert('meta[name="twitter:title"]', { tag: "meta", name: "twitter:title", content: fullTitle });
    upsert('meta[name="twitter:description"]', { tag: "meta", name: "twitter:description", content: desc });
    upsert('meta[name="twitter:image"]', { tag: "meta", name: "twitter:image", content: img });

    upsert('link[rel="alternate"][hreflang="de"]', { tag: "link", rel: "alternate", hreflang: "de", href: `${url}?lang=de` });
    upsert('link[rel="alternate"][hreflang="en"]', { tag: "link", rel: "alternate", hreflang: "en", href: `${url}?lang=en` });
    upsert('link[rel="alternate"][hreflang="x-default"]', { tag: "link", rel: "alternate", hreflang: "x-default", href: url });

    const orgLd = {
      "@context": "https://schema.org", "@type": "Organization", name: "JonuzovicDesign",
      url: BASE, description: desc,
      sameAs: [], areaServed: "DE",
    };
    let ld = document.getElementById("jd-jsonld");
    if (!ld) { ld = document.createElement("script"); ld.id = "jd-jsonld"; ld.type = "application/ld+json"; document.head.appendChild(ld); }
    ld.textContent = JSON.stringify(jsonLd || orgLd);
  }, [title, description, image, type, jsonLd, noindex, lang, pathname]);
  return null;
}
